import { gql } from "@apollo/client";

export const GET_CUSTOMERS = gql`
  query GetCustomers {
    getCustomers {
      id
      customer
      category
      contactName
      email
    }
  }
`;

// Sites and waste types are loaded only for the selected customer instead of
// for every customer the user has access to.
export const GET_CUSTOMER_FILTERS = gql`
  query GetCustomerFilters($customerId: ID!) {
    getCustomers(customerId: $customerId) {
      id
      wastes {
        wasteTypeId
        wasteType
      }
      sites {
        customerSite
        id
      }
    }
  }
`;

export const GET_USER_INFO = gql`
  query GetUserInfo {
    getCustomers {
      id
      customer
      displayUsername
      contactName
      email
      category
      pageAuth {
        financialPage
        weightingPage
        sustainabilityAudit
        reportingPage
      }
    }
  }
`;

export const GET_HOME_DATA = gql`
  query GetHomeData($input: DataInput) {
    getHomeData(input: $input) {
      dahsboardKPI {
        serviceRate
        totalWaste
        recycledWaste
        wasteForIncineration
        recyclingRate
        incinerationRate
        energyFromWaste
        CO2EmissionsGenerated
        CO2Saved
        treesSaved
      }
      dashboard {
        dataset
        customerSiteValue
        customerSiteLabel
        reducedWasteRate
        reducedWaste
        totalWeightCY
        totalWeightYA
        co2EmissionsGenerated
        emissionsRate
        percentage
        periodLabel
        periodValue
        recycledOrRecovered
        recyclingRate
        rorColorCode
        totalWeight
        wasteTypeLabel
        typeColorCode
        totalEmissions
        totalRecycled
        totalWaste
      }
    }
  }
`;

export const GET_SC_DATA_LIST = gql`
  query GetScDataList($siteId: [Int!]!) {
    getScDataList(siteId: $siteId) {
      audit_id
      DateCompleted
      CustomerSite
      Occupier
      carbone_template_id
      handlebar_uri
      sc_template_id
    }
  }
`;

export const GET_SC_DATA_LEADERBOARD = gql`
  query GetScDataLeaderboard($siteId: [Int!]!) {
    getScDataLeaderboard(siteId: $siteId) {
      audit_id
      DateCompleted
      CustomerSite
      Occupier
      PointPercentage
      MaxPoint
      Point
      ActionCount
    }
  }
`;

export const GET_SAFETY_CULTURE_HANDLEBAR = gql`
  query GetSafetyCultureHandlebar($auditId: ID!, $templateUri: String, $customerId: Int!) {
    getSafetyCultureHandlebar(auditId: $auditId, templateUri: $templateUri, customerId: $customerId)
  }
`;


export const GET_HOME_DATA_TIPPING = gql`
  query GetHomeDataTipping($input: DataInput) {
    getHomeTippingData(input: $input) {
      dashboardTipping {
        achievementRow {
          title
          value
        }
        feeData {
          periodLabel
          periodValue
          totalFee
          totalRebate
        }
        tonnageData {
          periodValue
          periodLabel
          totalWeight
        }
        levelData {
          level
          tonnage
        }
      }
    }
  }
`;

export const GET_SUGGESTIONS = gql`
  query GetRandomizedSuggestions {
    getRandomizedSuggestions {
      data {
        attributes {
          image {
            data {
              attributes {
                url
              }
            }
          }
          title
          description
          linkUrl
        }
      }
    }
  }
`;

export const GET_LINKEDIN_LAST_POST = gql`
  query Query {
    getLinkedinLastPost
  }
`;

export const GET_EVENTBRITE_DATA = gql`
  query getEventBanner {
    eventBanners {
      data {
        attributes {
          title
          link
          image {
            data {
              attributes {
                url
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_SUSTAINABILITY_BANNER_DATA = gql`
  query getSustainabilityBanner($pagination: PaginationArg) {
    sustainabilityBanners(sort: "createdAt:desc", pagination: $pagination) {
      data {
        id
        attributes {
          title
          link
          placement
          autoScroll
          displayTitle
          readMoreButton
          createdAt
          image {
            data {
              attributes {
                url
              }
            }
          }
        }
      }
      meta {
        pagination {
          page
          pageSize
          pageCount
          total
        }
      }
    }
  }
`;

export const GET_SUSTAINABILITY_ACHIEVEMENTS = gql`
  query getSustainabilityAchievements(
    $filters: SustainabilityAchievementFiltersInput
    $sort: [String]
    $pagination: PaginationArg
  ) {
    sustainabilityAchievements(filters: $filters, sort: $sort, pagination: $pagination) {
      data {
        id
        attributes {
          customerNumber
          siteNumber
          occupier
          awardName
          displayOrder
          isActive
          createdAt
          image {
            data {
              attributes {
                url
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_JOBS_ISSUES = gql`
  query GetJobIssueList($input: DataInput) {
    getJobIssueDocList(input: $input) {
      issues {
        jobDate
        site
        jobNumber
        jobIssueTypeName
        jobIssueTypeDescription
        responsibility
        issueStatus
        affectsServiceRate
      }
      jobs {
        jobDate
        site
        jobNumber
        arrTime
        depTime
        status
        service
        wasteType
        EWCCode
        weight
        quantity
        CO2Emissions
        downloadJobTicketFileName
        downloadJobTicketLink
      }
    }
  }
`;

export const GET_JOBS_TIPPING = gql`
  query GetJobTippingList($input: DataInput) {
    getJobTippingList(input: $input) {
      jobs {
        date
        vehicleRegNo
        round
        ticket
        arrivalTime
        departureTime
        turnaroundTime
        grossWeight
        tareWeight
        netWeight
        contaminationLevel
        gateFee
        totalFee
      }
      cardboard {
        date
        vehicleRegNo
        round
        ticket
        arrivalTime
        departureTime
        turnaroundTime
        grossWeight
        tareWeight
        netWeight
        contaminationLevel
        gateFee
        totalFee
      }
    }
  }
`;

export const GET_REPORT_DATA = gql`
  query GetReportData($customerId: ID!, $isYtd: Boolean) {
    getCustomers(customerId: $customerId) {
      id
      reports(isYTD: $isYtd) {
        periodLabel
        excelDownloadFilepath
        PDFViewFilepath
        PDFDownloadFilepath
      }
    }
  }
`;

export const GET_REPORT_DATA_TIPPING = gql`
  query GetReportDataTipping($customerId: ID!) {
    getCustomers(customerId: $customerId) {
      id
      reportsTipping {
        periodLabel
        excelDownloadFilepath
        PDFViewFilepath
        PDFDownloadFilepath
      }
    }
  }
`;

export const GET_INVOICE_DATA = gql`
  query GetInvoiceData($customerId: ID!, $input: InvoiceInput) {
    getCustomers(customerId: $customerId) {
      id
      customer
      invoices(input: $input) {
        invoiceNumber
        totalAmount
        invoiceType
        invoiceDate
        filePath
        creditedInvoiceNumber
      }
    }
  }
`;

export const GET_CUSTOMER_INVOICE_CHART = gql`
  query GetInvoiceChart($customerId: ID) {
    getInvoiceChart(customerId: $customerId) {
      MMMyyyy
      invoiceInclCreditNote
      creditNoteValue
    }
  }
`;

export const GET_DOCUMENT_DATA = gql`
  query Documents($input: DataInput) {
    getJobIssueDocList(input: $input) {
      documents {
        documentRef
        fileName
        endOfValidity
        documentType
        site
        filePath
        fileType
        source
      }
    }
  }
`;

export const GET_INSURANCE_DOCUMENT_DATA = gql`
  query InsuranceDocuments {
    getInsuranceDocList {
      documentType
      documentCategory
      docId
      fileName
      fileType
      periodFrom
      periodTo
      source
    }
  }
`;

export const DOWNLOAD_DOCUMENT_FILE = gql`
  query DownloadProtectedDocument($fileId: String!, $source: String!) {
    downloadProtectedDocument(fileId: $fileId, source: $source)
  }
`;

export const GET_AUDITS_DATA = gql`
  query GetAuditsData($customerId: ID!, $siteList: [Int]) {
    getCustomers(customerId: $customerId) {
      id
      customer
      sustainabilityAudit(siteList: $siteList) {
        documentType
        customerName
        fileName
        fileType
        lastModifiedDate
        filePath
      }
    }
  }
`;

export const GET_WEIGHTING_DATA = gql`
  query GetWeightingData($customerId: ID!) {
    getCustomers(customerId: $customerId) {
      id
      customer
      weightingPlatform {
        documentType
        customerName
        fileName
        fileType
        lastModifiedDate
        filePath
      }
    }
  }
`;
